import { useMemo, useEffect } from 'react';
import { Car } from 'lucide-react';
import { VehicleFields } from '@/features/contracts/components/form-fields';
import { FormInput } from '@/Components/shared/FormInput';
import { Tooltip } from '@/Components/ui/Tooltip';
import type { ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';
import { isPlausibleVehicleDate, maxVehicleYear } from '@/features/contracts/services/contractValidator';

interface Step2Props {
  formData: ContractFormState;
  updateFormData: (updates: Partial<ContractFormState>) => void;
  onCanProceedChange: (canProceed: boolean) => void;
  showErrors?: boolean;
}

/**
 * Step 2 : Véhicule (Informations + Prix de vente TTC + Kilométrage)
 */
export function Step2VehicleInfo({ formData, updateFormData, onCanProceedChange, showErrors = false }: Step2Props) {
  const canProceed = useMemo(() => {
    const hasIdentifier = !!formData.vehicle.registration || !!formData.vehicle.serialNumber;
    return hasIdentifier &&
           !!formData.vehicle.brand &&
           !!formData.vehicle.model &&
           !!formData.vehicle.entryIntoService &&
           isPlausibleVehicleDate(formData.vehicle.entryIntoService) &&
           formData.vehicleSellingPrice !== undefined &&
           formData.kmStart !== undefined;
  }, [formData]);

  useEffect(() => {
    onCanProceedChange(canProceed);
  }, [canProceed, onCanProceedChange]);

  const vehicleErrors = useMemo(() => {
    if (!showErrors) return undefined;
    const hasIdentifier = !!formData.vehicle.registration || !!formData.vehicle.serialNumber;
    const errors: Record<string, string> = {};
    if (!hasIdentifier) errors.registration = 'L\'immatriculation ou le VIN est requis';
    if (hasIdentifier && !formData.vehicle.brand) errors.registration = 'Veuillez cliquer sur Rechercher pour identifier le véhicule';
    if (!formData.vehicle.brand) errors.brand = 'La marque est requise';
    if (!formData.vehicle.model) errors.model = 'Le modèle est requis';
    if (!formData.vehicle.entryIntoService) {
      errors.entryIntoService = 'La date de mise en circulation est requise';
    } else if (!isPlausibleVehicleDate(formData.vehicle.entryIntoService)) {
      errors.entryIntoService = `Année invalide (attendue entre 1900 et ${maxVehicleYear()})`;
    }
    return Object.keys(errors).length > 0 ? errors : undefined;
  }, [showErrors, formData.vehicle]);

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4">
      <div className="tw-p-6 tw-space-y-5">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Car className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Informations du véhicule <Tooltip content="Identifiez le véhicule par immatriculation, VIN ou saisie manuelle. Ces informations figurent sur le contrat de garantie" /></h4>
        </div>

        <div id="vehicle-search-card">
          <VehicleFields
            vehicle={formData.vehicle}
            onChange={(vehicle) => updateFormData({ vehicle })}
            societyId={formData.societyId}
            errors={vehicleErrors}
          />
        </div>

        <div id="vehicle-pricing-block" className="tw-space-y-5">
          <div id="vehicle-selling-price-input" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
              Renseignez le prix de vente TTC du véhicule
            </p>
            <FormInput
              type="number"
              label="Prix de vente TTC du véhicule"
              value={formData.vehicleSellingPrice?.toString() || ''}
              onChange={(val) => updateFormData({ vehicleSellingPrice: val ? parseFloat(val) : undefined })}
              placeholder="Ex: 15000"
              required
              suffix="€"
              error={showErrors && formData.vehicleSellingPrice === undefined ? 'Le prix de vente est requis' : undefined}
              tooltip="Prix de vente TTC du véhicule affiché au client"
            />
          </div>
          <div id="km-start-input" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
              Indiquez le kilométrage actuel du véhicule au moment de la souscription
            </p>
            <FormInput
              type="number"
              label="Kilométrage au début de la couverture"
              value={formData.kmStart?.toString() || ''}
              onChange={(val) => updateFormData({ kmStart: val ? parseInt(val) : undefined })}
              placeholder="Ex: 50000"
              required
              suffix="km"
              error={showErrors && formData.kmStart === undefined ? 'Le kilométrage est requis' : undefined}
              tooltip="Kilométrage actuel du véhicule au moment de la souscription"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
